import { Card, Cluster, Container, Section, Stack, Status } from "@humaniti/ui";
import { Link } from "react-router-dom";
import { SEO, faqPageJsonLd } from "../lib/seo";

// Question/answer pairs for the FAQPage schema. Distilled from the long-form
// section copy below — kept here so the schema and the page text stay
// editable side-by-side. LLMs disproportionately quote FAQPage content
// because the Q→A mapping is unambiguous, so this is one of the highest-
// leverage structured-data emissions on the site.
const HOW_IT_WORKS_FAQ = [
  {
    question: "How does Humaniti verify labels?",
    answer:
      "Every label routes to N independent Humans (default 3, configurable up to 7). Their answers are weighted by per-skill Elo and aggregated via Dawid-Skene, MACE, and CAZ peer prediction. The aggregator returns a consensus label and a confidence score. Disagreement triggers Steward adjudication.",
  },
  {
    question: "What is the Tier system?",
    answer:
      "Four tiers: T0 (signed up, no tasks), T1 (phone verified, low-stakes work), T2 (ID verified via Sumsub, standard work), T3 (reputation verified, sensitive work + Steward eligibility). Each Human also carries a per-skill Elo that gates which work they can pick up within their tier.",
  },
  {
    question: "What does a Builder configure?",
    answer:
      "N-Human consensus (1, 3, 5, or 7), gold-question density, target inter-Human agreement threshold, language and region constraints on the qualified pool, and Steward escalation thresholds. All defaults are tuned per task type.",
  },
  {
    question: "What happens when Humans disagree?",
    answer:
      "Mandatory rejection reason from any Human who disagrees with a peer. 72-hour appeal window for the original Human. If escalated, the item routes to a fresh pool of Humans (blind re-annotation), then a T3 Steward adjudicates. The outcome is final and posts to the append-only audit log.",
  },
  {
    question: "How does Humaniti detect cheating?",
    answer:
      "Behavioral telemetry (keystroke timing, pointer paths, dwell), text-detection ensemble (Binoculars + Fast-DetectGPT + Ghostbuster + SynthID-Text) for free-response fields, gold-question injection, wallet and device clustering, periodic Steward audits. Phase 3 adds slashing of staked credits.",
  },
];

/**
 * Task lifecycle, rendered as a 7-step horizontal ladder. Was an SVG with
 * hardcoded x-coords + middle-anchored text — at the production column width
 * (1232px) the labels collided into each other and the leftmost label
 * ("Builder publishes") was clipped at the SVG's x=0 boundary. Switched to a
 * 7-column CSS grid so each label has its own column. The connecting rail is
 * stitched together from per-cell half-lines that butt up visually because
 * the grid columns are equal width — no absolute positioning across the
 * parent, no fragile coordinate math.
 */
const LIFECYCLE_STEPS = [
  "Builder publishes",
  "Assigned to N Humans",
  "Humans label",
  "Consensus aggregates",
  "Dispute → Steward",
  "Signed dataset",
  "Humans paid",
];

const TIERS: { tier: string; label: string; body: string }[] = [
  {
    tier: "T0",
    label: "Signed up",
    body: "Account created. Network browseable. No tasks until phone verified.",
  },
  {
    tier: "T1",
    label: "Phone verified",
    body: "Low-stakes work unlocked. Per-skill Elo seeded by qualification arena.",
  },
  {
    tier: "T2",
    label: "ID verified",
    body: "Standard work and higher per-label rates. Sumsub liveness + ID check.",
  },
  {
    tier: "T3",
    label: "Reputation verified",
    body: "Sensitive work, Steward eligibility, dispute adjudication. Highest rates.",
  },
];

const BUILDER_KNOBS = [
  {
    title: "N-Human consensus",
    body: "1 (cheap-draft) / 3 (default) / 5 (high-stakes) / 7 (max). Cost scales linearly with N. Confidence scales nonlinearly.",
  },
  {
    title: "Gold-question density",
    body: "Percent of items in each batch that are known-answer plants. Higher density tightens calibration; lower density saves cost.",
  },
  {
    title: "Target agreement threshold",
    body: "The inter-Human agreement (IAA) score below which a label routes to dispute. Tunable per project. Defaults tuned per task type.",
  },
  {
    title: "Language and region constraints",
    body: "Restrict the qualified pool by language, region, or device. Required for locale-sensitive work.",
  },
  {
    title: "Steward escalation thresholds",
    body: "Disagreement count, time-on-task, gold-question failure rate — any of these can trip Steward review. All defaults configurable.",
  },
];

const ANTI_GAMING = [
  "Behavioral telemetry — keystroke timing, pointer paths, dwell, scroll. Anomalies route to review.",
  "Text-detection ensemble — multi-model classification of generated text in free-response fields.",
  "Gold-question injection — known-answer items mixed continuously into every batch, blind to the Human.",
  "Wallet and device clustering — fingerprint and payout-rail signals collapse Sybil rings.",
  "Steward audits — T3 Humans periodically re-grade samples and review flagged sessions.",
  "Phase 3 slashing — staked credits are slashable for confirmed gaming once the credit unit ships.",
];

const DISPUTE_STEPS = [
  {
    n: "01",
    title: "Mandatory rejection reason",
    body: "Any Human who disagrees with a peer must record the reason. Free-text plus a structured taxonomy.",
  },
  {
    n: "02",
    title: "72-hour appeal window",
    body: "The original Human is notified. They can accept, revise, or escalate within 72 hours. Silence accepts the consensus.",
  },
  {
    n: "03",
    title: "Blind re-annotation",
    body: "If escalated, the item routes to a fresh pool of Humans who see the prompt without prior labels or rejection notes.",
  },
  {
    n: "04",
    title: "Steward adjudication",
    body: "A T3 Steward reviews the item, the consensus history, and the appeal. The outcome is final and posts to the audit log.",
  },
];

export const HowItWorksPage = () => (
  <>
    <SEO
      title="How it works — From request to receipt"
      description="How a label becomes verified. Task lifecycle, peer-consensus, dispute flow, the Steward review path. Quality is a protocol feature, not a vendor promise."
      path="/how-it-works"
      jsonLd={faqPageJsonLd(HOW_IT_WORKS_FAQ)}
    />
    {/* 1. Lede */}
    <Section>
      <Container size="full">
        <Stack gap="2xl">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-label)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-secondary)",
            }}
          >
            § How it works
          </span>
          <h1
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-display)",
              lineHeight: "var(--lh-display)",
              letterSpacing: "-0.02em",
              maxWidth: 960,
            }}
          >
            How a label becomes verified<span style={{ color: "var(--teal)" }}>.</span>
          </h1>
          <p
            style={{
              margin: 0,
              maxWidth: 720,
              fontSize: "var(--fs-body-lg)",
              lineHeight: "var(--lh-body-lg)",
              color: "var(--fg-secondary)",
            }}
          >
            A Builder publishes a task. Verified Humans label it. Consensus aggregates the labels.
            Per-skill Elo weights each Human's vote. A Steward adjudicates disagreement. Gold
            questions catch drift. The signed dataset ships. The Humans get paid. That is the entire
            loop, end to end.
          </p>
        </Stack>
      </Container>
    </Section>

    {/* 2. Task lifecycle */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-label)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-secondary)",
            }}
          >
            § 01 — Lifecycle
          </span>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-h1)",
              lineHeight: "var(--lh-h1)",
              letterSpacing: "-0.01em",
            }}
          >
            From request to receipt<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <div
            style={{ overflowX: "auto", paddingBottom: "var(--space-2)" }}
            aria-label="Task lifecycle"
          >
            <ol
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${LIFECYCLE_STEPS.length}, 1fr)`,
                width: "100%",
                minWidth: 760,
                margin: 0,
                padding: 0,
                listStyle: "none",
              }}
            >
              {LIFECYCLE_STEPS.map((label, i, arr) => {
                const isFirst = i === 0;
                const isLast = i === arr.length - 1;
                return (
                  <li
                    key={label}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: "var(--fs-label)",
                        letterSpacing: "0.08em",
                        color: "var(--fg-secondary)",
                        paddingBottom: "var(--space-3)",
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    {/* Rail row: per-cell half-lines + centered dot. Half-lines
                        from adjacent cells butt up visually (equal-width grid
                        columns) so the rail reads as continuous. First cell
                        suppresses its left half, last suppresses its right. */}
                    <div
                      style={{
                        position: "relative",
                        height: 14,
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: 0,
                          width: "calc(50% - 7px)",
                          height: 1,
                          background: isFirst ? "transparent" : "var(--border-hairline)",
                          transform: "translateY(-50%)",
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          top: "50%",
                          right: 0,
                          width: "calc(50% - 7px)",
                          height: 1,
                          background: isLast ? "transparent" : "var(--border-hairline)",
                          transform: "translateY(-50%)",
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: 14,
                          height: 14,
                          borderRadius: "50%",
                          background: "var(--paper)",
                          border: "2px solid var(--ink)",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        paddingTop: "var(--space-3)",
                        paddingLeft: "var(--space-2)",
                        paddingRight: "var(--space-2)",
                        fontSize: "var(--fs-body)",
                        lineHeight: "var(--lh-body)",
                        color: "var(--ink)",
                      }}
                    >
                      {label}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
          <p
            style={{
              margin: 0,
              fontSize: "var(--fs-body-lg)",
              lineHeight: "var(--lh-body-lg)",
              maxWidth: 720,
              color: "var(--fg-secondary)",
            }}
          >
            A Builder defines the task — its prompt, its label schema, its consensus N, its quality
            thresholds — and publishes. The qualified pool is filtered by tier, skill, language, and
            region. Each item routes to N Humans independently. The aggregator weighs each label by
            Elo, accounts for known per-Human bias, and returns a consensus label plus a confidence
            score.
          </p>
          <p
            style={{
              margin: 0,
              fontSize: "var(--fs-body-lg)",
              lineHeight: "var(--lh-body-lg)",
              maxWidth: 720,
              color: "var(--fg-secondary)",
            }}
          >
            If confidence is high, the label ships into the signed dataset and the Humans get paid.
            If confidence is low or a Human appeals, the item routes to a Steward. The Steward's
            outcome is final and writes to the append-only audit log.
          </p>
        </Stack>
      </Container>
    </Section>

    {/* 3. The Humans (tiers + Elo) */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-label)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-secondary)",
            }}
          >
            § 02 — The Humans
          </span>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-h1)",
              lineHeight: "var(--lh-h1)",
              letterSpacing: "-0.01em",
            }}
          >
            Quality is a ladder<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <Cluster gap="md" align="start">
            {TIERS.map((t) => (
              <Card key={t.tier} style={{ flex: 1, minWidth: 220 }}>
                <Status variant="accent">{t.tier}</Status>
                <h3
                  style={{
                    margin: "var(--space-3) 0 var(--space-2)",
                    fontSize: "var(--fs-h3)",
                    fontFamily: "var(--font-sans)",
                    fontWeight: 500,
                  }}
                >
                  {t.label}
                </h3>
                <p
                  style={{
                    margin: 0,
                    color: "var(--fg-secondary)",
                    fontSize: "var(--fs-body)",
                    lineHeight: "var(--lh-body)",
                  }}
                >
                  {t.body}
                </p>
              </Card>
            ))}
          </Cluster>
          <p
            style={{
              margin: 0,
              fontSize: "var(--fs-body-lg)",
              lineHeight: "var(--lh-body-lg)",
              maxWidth: 720,
              color: "var(--fg-secondary)",
            }}
          >
            On top of the four tiers, each Human carries a per-skill Elo — bbox, Text NER, audio
            transcription, RLHF preference, and so on. New Humans seed their Elo through a
            qualification arena of gold-graded items. Verified accuracy raises the score. Drift
            lowers it. Higher Elo unlocks higher-paying work in that specific skill.
          </p>
        </Stack>
      </Container>
    </Section>

    {/* 4. The Builders (configuration knobs) */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-label)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-secondary)",
            }}
          >
            § 03 — The Builders
          </span>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-h1)",
              lineHeight: "var(--lh-h1)",
              letterSpacing: "-0.01em",
            }}
          >
            What a Builder controls<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {BUILDER_KNOBS.map((k, i) => (
              <li
                key={k.title}
                style={{
                  display: "grid",
                  gridTemplateColumns: "64px 1fr",
                  gap: "var(--space-5)",
                  padding: "var(--space-5) 0",
                  borderTop: "1px solid var(--border-hairline)",
                }}
              >
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: "var(--fs-small)",
                    color: "var(--fg-secondary)",
                    paddingTop: "var(--space-1)",
                  }}
                >
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div>
                  <div
                    style={{
                      fontFamily: "var(--font-sans)",
                      fontWeight: 500,
                      fontSize: "20px",
                      lineHeight: "28px",
                      marginBottom: "var(--space-2)",
                    }}
                  >
                    {k.title}.
                  </div>
                  <div
                    style={{
                      fontSize: "var(--fs-body)",
                      lineHeight: "var(--lh-body)",
                      color: "var(--fg-secondary)",
                      maxWidth: 640,
                    }}
                  >
                    {k.body}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </Stack>
      </Container>
    </Section>

    {/* 5. The aggregator (consensus) */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-label)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-secondary)",
            }}
          >
            § 04 — Consensus
          </span>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-h1)",
              lineHeight: "var(--lh-h1)",
              letterSpacing: "-0.01em",
            }}
          >
            Disagreement is data<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: "var(--fs-body-lg)",
              lineHeight: "var(--lh-body-lg)",
              maxWidth: 720,
              color: "var(--fg-secondary)",
            }}
          >
            Plain majority vote is a bad aggregator. It treats every Human as equally accurate and
            every disagreement as noise. The Network treats disagreement as signal. Three families
            of methods do the work — Dawid-Skene estimates each Human's confusion matrix from their
            full label history and weighs their vote accordingly. MACE separates random spamming
            from honest disagreement. CAZ peer-prediction rewards Humans for being informative about
            what other Humans will say, which makes random clicking a losing strategy.
          </p>
          <p
            style={{
              margin: 0,
              fontSize: "var(--fs-body-lg)",
              lineHeight: "var(--lh-body-lg)",
              maxWidth: 720,
              color: "var(--fg-secondary)",
            }}
          >
            The aggregator returns two numbers — the consensus label and a confidence score. The
            confidence score is what trips dispute, gold-question audits, and Steward escalation.
            The math stays inside the Network. The Builder sees the label, the confidence, the audit
            trail, and the receipts.
          </p>
        </Stack>
      </Container>
    </Section>

    {/* 6. Anti-gaming layer */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-label)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-secondary)",
            }}
          >
            § 05 — Anti-gaming
          </span>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-h1)",
              lineHeight: "var(--lh-h1)",
              letterSpacing: "-0.01em",
            }}
          >
            Why this can't be cheated<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <ul
            style={{
              margin: 0,
              paddingLeft: "var(--space-5)",
              fontSize: "var(--fs-body-lg)",
              lineHeight: "var(--lh-body-lg)",
              maxWidth: 720,
            }}
          >
            {ANTI_GAMING.map((item) => (
              <li key={item} style={{ padding: "var(--space-1) 0" }}>
                {item}
              </li>
            ))}
          </ul>
        </Stack>
      </Container>
    </Section>

    {/* 7. Dispute flow */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-label)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-secondary)",
            }}
          >
            § 06 — Disputes
          </span>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-h1)",
              lineHeight: "var(--lh-h1)",
              letterSpacing: "-0.01em",
            }}
          >
            What happens when Humans disagree<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {DISPUTE_STEPS.map((s) => (
              <li
                key={s.n}
                style={{
                  display: "grid",
                  gridTemplateColumns: "64px 1fr",
                  gap: "var(--space-5)",
                  padding: "var(--space-5) 0",
                  borderTop: "1px solid var(--border-hairline)",
                }}
              >
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: "var(--fs-small)",
                    color: "var(--teal)",
                    paddingTop: "var(--space-1)",
                  }}
                >
                  {s.n}
                </div>
                <div>
                  <div
                    style={{
                      fontFamily: "var(--font-sans)",
                      fontWeight: 500,
                      fontSize: "20px",
                      lineHeight: "28px",
                      marginBottom: "var(--space-2)",
                    }}
                  >
                    {s.title}.
                  </div>
                  <div
                    style={{
                      fontSize: "var(--fs-body)",
                      lineHeight: "var(--lh-body)",
                      color: "var(--fg-secondary)",
                      maxWidth: 640,
                    }}
                  >
                    {s.body}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </Stack>
      </Container>
    </Section>

    {/* 8. Closing CTA */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="md">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-label)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-secondary)",
            }}
          >
            § 07 — Methodology
          </span>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-h1)",
              lineHeight: "var(--lh-h1)",
              letterSpacing: "-0.01em",
            }}
          >
            Read deeper<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: "var(--fs-body-lg)",
              lineHeight: "var(--lh-body-lg)",
              maxWidth: 720,
              color: "var(--fg-secondary)",
            }}
          >
            The shape above is the public model. The implementation has more — confidence
            calibration curves, Elo prior selection, gold-question generation policy. The Field
            notes go further into the math.
          </p>
          <Link to="/notes/why-consensus-matters">Read the methodology →</Link>
        </Stack>
      </Container>
    </Section>
  </>
);
